import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptors
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('booka_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('booka_token');
      localStorage.removeItem('booka_user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  getMe: () => apiClient.get('/auth/me'),
  updateProfile: (data) => apiClient.put('/auth/profile', data),
  changePassword: (data) => apiClient.post('/auth/change-password', data),
  getNotificationPreferences: () => apiClient.get('/auth/notification-preferences'),
  updateNotificationPreferences: (data) => apiClient.put('/auth/notification-preferences', data),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => apiClient.post('/auth/reset-password', { token, newPassword })
};

// Centurion (Founding Members)
export const centurionAPI = {
  getCount: () => apiClient.get('/centurions/count'),
  getList: () => apiClient.get('/centurions/list'),
  getPricing: () => apiClient.get('/centurions/pricing')
};

// Referral System
export const referralAPI = {
  validate: (code) => apiClient.get(`/referral/validate/${code}`),
  getMyInfo: () => apiClient.get('/referral/my-info'),
  adminUpdateCredits: (businessId, credits) => apiClient.post(`/admin/referral-credits/${businessId}`, { credits }),
  adminGetAllBusinesses: () => apiClient.get('/admin/businesses-with-referrals'),
  adminMigrateReferralCodes: () => apiClient.post('/admin/migrate-referral-codes'),
  adminGetStats: () => apiClient.get('/admin/referral-stats'),
  adminProcessCreditBilling: () => apiClient.post('/admin/process-credit-billing'),
  adminResumeStripeBilling: (businessId) => apiClient.post(`/admin/resume-stripe-billing/${businessId}`)
};

// Billing
export const billingAPI = {
  getInvoices: () => apiClient.get('/billing/invoices'),
  getUpcoming: () => apiClient.get('/billing/upcoming'),
  enableInvoiceEmails: () => apiClient.post('/billing/enable-invoice-emails'),
  processWithCredits: () => apiClient.post('/billing/process-with-credits'),
  getCreditHistory: () => apiClient.get('/billing/credit-history'),
  reactivateAccount: (paymentMethodId) => apiClient.post('/billing/reactivate-account', { paymentMethodId })
};

// Stripe
export const stripeAPI = {
  createSetupIntent: () => apiClient.post('/stripe/create-setup-intent')
};

// Businesses
export const businessAPI = {
  getAll: () => apiClient.get('/businesses'),
  getById: (id) => apiClient.get(`/businesses/${id}`),
  getServices: (id) => apiClient.get(`/businesses/${id}/services`),
  getStaff: (id) => apiClient.get(`/businesses/${id}/staff`),
  getMine: () => apiClient.get('/my-business'),
  updateMine: (data) => apiClient.put('/my-business', data),
  uploadPhoto: (formData) => apiClient.post('/upload-business-photo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
};

// Services
export const serviceAPI = {
  create: (data) => apiClient.post('/services', data),
  getMyServices: () => apiClient.get('/my-services'),
  update: (id, data) => apiClient.put(`/services/${id}`, data),
  delete: (id) => apiClient.delete(`/services/${id}`)
};

// Staff
export const staffAPI = {
  getAll: () => apiClient.get('/staff'),
  create: (data) => apiClient.post('/staff', data),
  update: (id, data) => apiClient.put(`/staff/${id}`, data),
  delete: (id) => apiClient.delete(`/staff/${id}`),
  previewSubscriptionChange: (action) => apiClient.get(`/staff/subscription-preview?action=${action}`),
  getFutureBookingsCount: (id) => apiClient.get(`/staff/${id}/future-bookings-count`)
};

// Revenue
export const revenueAPI = {
  getSummary: () => apiClient.get('/revenue'),
  getByStaff: () => apiClient.get('/revenue/by-staff'),
  getByService: () => apiClient.get('/revenue/by-service'),
  getMonthly: () => apiClient.get('/revenue/monthly')
};

// Payouts
export const payoutAPI = {
  getHistory: () => apiClient.get('/payouts')
};

// Analytics
export const analyticsAPI = {
  getAdvanced: () => apiClient.get('/analytics')
};

// Availability
export const availabilityAPI = {
  get: (businessId, date, staffId = null) => {
    if (staffId) {
      return apiClient.get(`/availability/${businessId}/${staffId}/${date}`);
    }
    return apiClient.get(`/availability/${businessId}/${date}`);
  },
  set: (businessId, date, slots, staffId = null) => {
    let url = `/availability?business_id=${businessId}&date=${date}`;
    if (staffId) {
      url += `&staff_id=${staffId}`;
    }
    return apiClient.post(url, slots);
  }
};

// Appointments
export const appointmentAPI = {
  create: (data) => apiClient.post('/appointments', data),
  bookForCustomer: (data) => apiClient.post('/appointments/book-for-customer', data),
  getMine: () => apiClient.get('/my-appointments'),
  getBusinessAppointments: () => apiClient.get('/business-appointments'),
  getBusinessCustomers: () => apiClient.get('/business-customers'),
  deleteCustomer: (customerId) => apiClient.delete(`/business-customers/${customerId}`),
  updateStatus: (id, status) => apiClient.put(`/appointments/${id}/status?status=${status}`),
  cancel: (id) => apiClient.put(`/appointments/${id}/cancel`),
  markAttendance: (id, attendance) => apiClient.put(`/appointments/${id}/attendance?attendance=${attendance}`)
};

// Reviews
export const reviewAPI = {
  create: (data) => apiClient.post('/reviews', data),
  getForBusiness: (businessId) => apiClient.get(`/businesses/${businessId}/reviews`),
  getMyReviews: () => apiClient.get('/my-reviews'),
  getBusinessOwnerReviews: () => apiClient.get('/business/reviews'),
  delete: (reviewId) => apiClient.delete(`/reviews/${reviewId}`),
  adminGetAll: () => apiClient.get('/admin/reviews')
};

// Payments
export const paymentAPI = {
  validateOfferCode: (code) => apiClient.post('/payments/validate-offer-code', { code }),
  createCheckout: (data) => apiClient.post('/payments/create-checkout', data),
  getStatus: (sessionId) => apiClient.get(`/payments/status/${sessionId}`),
  completeBooking: (data) => apiClient.post('/payments/complete-booking', data)
};

// Stripe Connect
export const stripeConnectAPI = {
  createAccount: () => apiClient.post('/stripe-connect/create-account'),
  getStatus: () => apiClient.get('/stripe-connect/status'),
  getDashboardLink: () => apiClient.post('/stripe-connect/dashboard-link')
};

// Subscriptions
export const subscriptionAPI = {
  getMine: () => apiClient.get('/my-subscription'),
  getPricing: () => apiClient.get('/subscription/pricing'),
  setupPayment: () => apiClient.post('/subscription/setup-payment'),
  cancel: () => apiClient.post('/subscription/cancel')
};

// Notifications
export const notificationAPI = {
  getAll: () => apiClient.get('/notifications'),
  markRead: (id) => apiClient.put(`/notifications/${id}/read`),
  markAllRead: () => apiClient.put('/notifications/read-all')
};

// Admin
export const adminAPI = {
  getStats: () => apiClient.get('/admin/stats'),
  
  // Users
  getUsers: () => apiClient.get('/admin/users'),
  getUser: (id) => apiClient.get(`/admin/users/${id}`),
  updateUser: (id, data) => apiClient.put(`/admin/users/${id}`, data),
  deleteUser: (id) => apiClient.delete(`/admin/users/${id}`),
  
  // Businesses
  getBusinesses: () => apiClient.get('/admin/businesses'),
  updateBusiness: (id, data) => apiClient.put(`/admin/businesses/${id}`, data),
  deleteBusiness: (id) => apiClient.delete(`/admin/businesses/${id}`),
  
  // Subscriptions
  getSubscriptions: () => apiClient.get('/admin/subscriptions'),
  updateSubscription: (id, data) => apiClient.put(`/admin/subscriptions/${id}`, data),
  grantFreeAccess: (id, grant) => apiClient.put(`/admin/subscriptions/${id}/free-access?grant=${grant}`),
  
  // Appointments
  getAppointments: () => apiClient.get('/admin/appointments'),
  refundAppointment: (id, amount) => apiClient.put(`/admin/appointments/${id}/refund?amount=${amount}`),
  
  // Trials
  getTrialStatus: () => apiClient.get('/admin/trial-status'),
  sendTrialReminders: () => apiClient.post('/admin/send-trial-reminders')
};

export default apiClient;
